'use client';

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import { fetchRecruiterCallVoiceToken } from '@hooks/useSwrApi/endpoints/recruiter-call';
import { recordWebRtcEvent } from '@services/recruiterCallObservability/webRtcEventSink';
import type { RecruiterCallConnectParams } from '@type/recruiter-call';

export type TwilioDeviceState = 'idle' | 'registering' | 'ready' | 'connecting' | 'in-call' | 'error';

/**
 * Semantic classification of the last error the hook surfaced. Lets the
 * caller render a dedicated recovery UI for the ones that have a specific
 * user-facing remedy (mic-permission blocked → surface OS/browser fix
 * steps + Retry) without pattern-matching on `.message` strings.
 */
export type RecruiterCallErrorKind = 'mic-permission' | 'other';

interface UseTwilioDeviceReturn {
  state: TwilioDeviceState;
  error: string | null;
  errorKind: RecruiterCallErrorKind | null;
  clearError: () => void;
  callSid: string | null;
  isMuted: boolean;
  callDuration: number;
  connect: (params: RecruiterCallConnectParams) => Promise<void>;
  disconnect: () => void;
  toggleMute: () => void;
  /** Audio output device picker state — see hook docstring. */
  outputDevices: MediaDeviceInfo[];
  selectedOutputDeviceId: string | null;
  isOutputSelectionSupported: boolean;
  selectOutputDevice: (deviceId: string | null) => Promise<void>;
  testOutputDevice: () => Promise<void>;
}

// Twilio Voice SDK error codes that map to a mic-acquisition failure the
// recruiter can act on. All three surface with the same recovery steps
// (check browser mic permission, OS mic privacy, other apps holding the
// mic), so we bucket them together:
//   - 31401 `PermissionDeniedError` — browser or OS denied getUserMedia.
//   - 31208 `UserMediaFailed` — SDK-level acquisition failed (device busy,
//     no input device selected, etc.).
//   - 31402 `AcquisitionFailedError` — "permissions granted but getting
//     the media failed" (bad constraints, driver / hardware issue).
//     Observed 2026-09-01 on the EASY CREDIT account: 17 events in one
//     recruiter session, no calls placeable — the exact class the
//     recovery panel is meant to catch, but was missed by the initial
//     bucket.
const MIC_PERMISSION_CODES = new Set<number>([31401, 31208, 31402]);

const classifyError = (err: unknown): RecruiterCallErrorKind => {
  if (err instanceof DOMException && err.name === 'NotAllowedError') {
    return 'mic-permission';
  }
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === 'number' && MIC_PERMISSION_CODES.has(code)) {
      return 'mic-permission';
    }
  }
  return 'other';
};

const errorCode = (err: unknown): number | null => {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === 'number') return code;
  }
  return null;
};

// Twilio's `AcquisitionFailedError (31402)` is a catch-all — the SDK
// wraps the underlying `DOMException` from `getUserMedia()` in
// `err.originalError`. The DOMException's `name` narrows the actual
// cause enough to route support: `NotFoundError` (no mic), `NotReadableError`
// (device held by another app), `OverconstrainedError` (Twilio constraints
// don't match hardware), `AbortError` (hw/driver failure), `SecurityError`
// (blocked by browser policy / sandboxed iframe). For DOMExceptions
// thrown directly from `connect()`'s catch, `err.name` IS the DOMException
// name and no unwrap is needed. Returns null when neither shape matches.
const originalErrorName = (err: unknown): string | null => {
  if (err instanceof DOMException) {
    return err.name;
  }
  if (typeof err === 'object' && err !== null && 'originalError' in err) {
    const orig = (err as { originalError?: unknown }).originalError;
    if (orig instanceof DOMException) {
      return orig.name;
    }
    if (typeof orig === 'object' && orig !== null && 'name' in orig) {
      const name = (orig as { name?: unknown }).name;
      if (typeof name === 'string') return name;
    }
  }
  return null;
};

// Snapshot of the audio-input devices at error time. Distinguishes "no
// mic detected at all" (empty list → probably a hardware / driver issue)
// from "mic present but acquisition failed" (list has entries → likely
// a held-by-another-app or constraints issue). Labels are populated only
// when mic permission was previously granted; empty labels are itself
// signal (permission not granted yet).
const captureMediaSnapshot = async (): Promise<Record<string, unknown>> => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return { available: false };
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter((d) => d.kind === 'audioinput');
    return {
      available: true,
      audioInputCount: inputs.length,
      audioInputLabels: inputs.map((d) => d.label || null),
    };
  } catch {
    return { available: false };
  }
};

// Persisted across sessions — Chrome's per-origin deviceId is stable until the
// user clears cookies, so a saved USB-headset id will still resolve tomorrow.
const OUTPUT_DEVICE_STORAGE_KEY = 'bj:recruiter-call:speaker-device-id';

/**
 * Bootstraps a Twilio.Device for the logged-in recruiter and exposes the
 * softphone lifecycle as React state.
 *
 * The Device is created lazily on first `connect()` and torn down on
 * component unmount so we don't keep an idle WebRTC session open per
 * candidate profile tab.
 *
 * ## Audio output device selection
 *
 * By default the Twilio Voice JS SDK plays incoming audio through the
 * browser's default output device — which on Windows is very often the
 * laptop speakers, NOT the USB headset the recruiter's mic is on. Result:
 * they speak into the headset (mic works, candidate hears them, recording
 * captures both sides) but hear silence during the call because playback
 * goes to muted / docked / turned-off speakers. Diagnosed 2026-08-14 from
 * a support ticket where the recruiter tried different headsets in vain.
 *
 * This hook exposes {@link UseTwilioDeviceReturn.selectOutputDevice} so a
 * settings UI can pin playback (both call audio and ringtone) to a specific
 * device, persisted per-origin in localStorage. On the next session the
 * saved deviceId is re-applied after `device.register()`.
 *
 * Devices enumerated via `navigator.mediaDevices.enumerateDevices()` are
 * unlabeled until mic permission is granted; labels populate automatically
 * once the first `connect()` triggers Twilio's `getUserMedia` prompt.
 */
export const useTwilioDevice = (): UseTwilioDeviceReturn => {
  const [state, setState] = useState<TwilioDeviceState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<RecruiterCallErrorKind | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedOutputDeviceId, setSelectedOutputDeviceIdState] = useState<string | null>(null);
  const [isOutputSelectionSupported, setIsOutputSelectionSupported] = useState(true);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Held in a ref (not just state) so `ensureDevice` — which is only
  // recreated on mount — reads the current value at register time.
  const selectedOutputDeviceIdRef = useRef<string | null>(null);

  const clearDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearDurationTimer();
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
    }
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
    }
    setCallSid(null);
    setIsMuted(false);
    setCallDuration(0);
  }, [clearDurationTimer]);

  useEffect(() => teardown, [teardown]);

  // Load the saved output device preference + populate the picker on mount.
  // `enumerateDevices()` returns unlabeled entries pre-permission; labels
  // fill in once Device registration triggers the mic prompt.
  useEffect(() => {
    let cancelled = false;

    try {
      const saved = window.localStorage.getItem(OUTPUT_DEVICE_STORAGE_KEY);
      if (saved) {
        selectedOutputDeviceIdRef.current = saved;
        setSelectedOutputDeviceIdState(saved);
      }
    } catch { /* localStorage may be blocked (Safari private mode, etc.) */ }

    const refresh = async () => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
        return;
      }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        setOutputDevices(devices.filter((d) => d.kind === 'audiooutput'));
      } catch {
        if (!cancelled) setOutputDevices([]);
      }
    };
    void refresh();

    const nmd = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    nmd?.addEventListener?.('devicechange', refresh);
    return () => {
      cancelled = true;
      nmd?.removeEventListener?.('devicechange', refresh);
    };
  }, []);

  const applySpeakerSelection = useCallback(async (device: Device, deviceId: string | null): Promise<void> => {
    if (!device.audio) return;
    setIsOutputSelectionSupported(device.audio.isOutputSelectionSupported);
    if (!device.audio.isOutputSelectionSupported || !deviceId) return;
    if (!device.audio.availableOutputDevices.has(deviceId)) return;
    try {
      await device.audio.speakerDevices.set(deviceId);
      await device.audio.ringtoneDevices.set(deviceId);
    } catch {
      // Device disappeared between the availability check and the set call
      // (unplugged headset, etc.). Fall back silently to the default.
    }
  }, []);

  const ensureDevice = useCallback(async (): Promise<Device> => {
    if (deviceRef.current) {
      return deviceRef.current;
    }
    setState('registering');
    const { token } = await fetchRecruiterCallVoiceToken();
    if (!token) {
      throw new Error('RecruiterCall: voice-token endpoint returned no token');
    }
    const device = new Device(token, {
      // logLevel: 'debug', // enable when debugging WebRTC issues
    });
    device.on('error', (twilioError: Error) => {
      setError(twilioError.message);
      setErrorKind(classifyError(twilioError));
      setState('error');
      // Device-level error has no Call context yet; emit with empty
      // provider_call_sid — the backend accepts these and analysis
      // correlates via user_agent + received_at. Enrichment (originalError
      // name + media snapshot) fires async and shares the batch with the
      // synchronous state updates — recordWebRtcEvent is fire-and-forget.
      void (async () => {
        recordWebRtcEvent({
          providerCallSid: '',
          eventType: 'device-error',
          eventBody: {
            message: twilioError.message,
            name: twilioError.name,
            code: errorCode(twilioError),
            originalErrorName: originalErrorName(twilioError),
            mediaSnapshot: await captureMediaSnapshot(),
          },
        });
      })();
    });
    await device.register();
    // Now that `device.audio.availableOutputDevices` is populated (mic
    // permission is granted as a side effect of register), re-emit the list
    // with labels + apply the saved selection.
    if (device.audio) {
      setOutputDevices(Array.from(device.audio.availableOutputDevices.values()));
      await applySpeakerSelection(device, selectedOutputDeviceIdRef.current);
    }
    deviceRef.current = device;
    setState('ready');
    return device;
  }, [applySpeakerSelection]);

  const attachCallListeners = useCallback((call: Call) => {
    // Shared shortcut — the Call's SID appears on `call.parameters.CallSid`
    // once Twilio has assigned one (post-accept for outbound calls). Empty
    // string when not yet known — analysis handles both.
    const sid = (): string => call.parameters.CallSid ?? '';

    call.on('accept', () => {
      setState('in-call');
      setCallSid(call.parameters.CallSid ?? null);
      const startedAt = Date.now();
      clearDurationTimer();
      durationTimerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - startedAt) / 1000));
      }, 1000);
      recordWebRtcEvent({ providerCallSid: sid(), eventType: 'accept', eventBody: {} });
    });
    call.on('disconnect', () => {
      clearDurationTimer();
      callRef.current = null;
      setState('ready');
      recordWebRtcEvent({ providerCallSid: sid(), eventType: 'disconnect', eventBody: {} });
    });
    call.on('cancel', () => {
      clearDurationTimer();
      callRef.current = null;
      setState('ready');
      recordWebRtcEvent({ providerCallSid: sid(), eventType: 'cancel', eventBody: {} });
    });
    call.on('error', (twilioError: Error) => {
      clearDurationTimer();
      setError(twilioError.message);
      setErrorKind(classifyError(twilioError));
      setState('error');
      const callSid = sid();
      void (async () => {
        recordWebRtcEvent({
          providerCallSid: callSid,
          eventType: 'call-error',
          eventBody: {
            message: twilioError.message,
            name: twilioError.name,
            code: errorCode(twilioError),
            originalErrorName: originalErrorName(twilioError),
            mediaSnapshot: await captureMediaSnapshot(),
          },
        });
      })();
    });
    call.on('mute', (muted: boolean) => setIsMuted(muted));
    // The two events that catch the audit's mic-drop fingerprint:
    // `low-mic-level`, `constant-audio-input-level`, `high-rtt`,
    // `high-packet-loss`, codec renegotiation. Store the raw name +
    // data verbatim — new SDK versions may add new warning names, and
    // the sink is deliberately forward-compat.
    call.on('warning', (name: string, data?: unknown) => {
      recordWebRtcEvent({
        providerCallSid: sid(),
        eventType: 'warning',
        eventBody: { name, data: data ?? null },
      });
    });
    call.on('warning-cleared', (name: string) => {
      recordWebRtcEvent({
        providerCallSid: sid(),
        eventType: 'warning-cleared',
        eventBody: { name },
      });
    });
  }, [clearDurationTimer]);

  const connect = useCallback(async (params: RecruiterCallConnectParams) => {
    setError(null);
    setErrorKind(null);
    try {
      const device = await ensureDevice();
      setState('connecting');
      const call = await device.connect({ params });
      callRef.current = call;
      attachCallListeners(call);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setErrorKind(classifyError(e));
      setState('error');
      // Failures thrown from `ensureDevice()` / `device.connect()` never
      // reach the `device.on('error')` / `call.on('error')` handlers —
      // record here so pre-Call failures (raw getUserMedia denials, token
      // fetch errors) still show up in observability.
      void (async () => {
        recordWebRtcEvent({
          providerCallSid: '',
          eventType: 'connect-error',
          eventBody: {
            message: e instanceof Error ? e.message : String(e),
            name: e instanceof Error ? e.name : null,
            code: errorCode(e),
            originalErrorName: originalErrorName(e),
            mediaSnapshot: await captureMediaSnapshot(),
          },
        });
      })();
    }
  }, [attachCallListeners, ensureDevice]);

  const clearError = useCallback(() => {
    setError(null);
    setErrorKind(null);
    // Flip back to a state the retry effect / manual connect() accepts.
    // Device registration may still be intact — pick `ready` when we have
    // a device, `idle` when we don't. Doesn't touch the Twilio Device or
    // Call objects; those are already torn down or never opened when we
    // hit the error branch.
    setState(deviceRef.current ? 'ready' : 'idle');
  }, []);

  const disconnect = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    const next = !callRef.current.isMuted();
    callRef.current.mute(next);
  }, []);

  const selectOutputDevice = useCallback(async (deviceId: string | null): Promise<void> => {
    selectedOutputDeviceIdRef.current = deviceId;
    setSelectedOutputDeviceIdState(deviceId);
    try {
      if (deviceId) {
        window.localStorage.setItem(OUTPUT_DEVICE_STORAGE_KEY, deviceId);
      } else {
        window.localStorage.removeItem(OUTPUT_DEVICE_STORAGE_KEY);
      }
    } catch { /* localStorage blocked — the ref still drives the current session */ }
    if (deviceRef.current) {
      await applySpeakerSelection(deviceRef.current, deviceId);
    }
  }, [applySpeakerSelection]);

  const testOutputDevice = useCallback(async (): Promise<void> => {
    // Twilio plays a short beep through the currently-configured output
    // device. Requires the Device to be registered — but registering is
    // the side effect that grants mic permission + populates device
    // labels, so we do it on-demand here rather than eagerly on modal
    // open (an unprompted mic dialog would feel invasive). Users who
    // click Test before ever placing a call see the permission prompt
    // in the context they expect, then hear the beep — and the device
    // dropdown fills in with real names for the follow-up call.
    try {
      const device = await ensureDevice();
      await device.audio?.speakerDevices.test();
    } catch {
      // Non-fatal; usually a codec/autoplay quirk on the test tone itself,
      // or a mic-permission denial (already surfaced via `state === 'error'`).
    }
  }, [ensureDevice]);

  return {
    state,
    error,
    errorKind,
    clearError,
    callSid,
    isMuted,
    callDuration,
    connect,
    disconnect,
    toggleMute,
    outputDevices,
    selectedOutputDeviceId,
    isOutputSelectionSupported,
    selectOutputDevice,
    testOutputDevice,
  };
};
